import { T } from '@tolgee/react';
import { Button, Radio, Spin } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useAssignClass from '../../../hooks/useAssignClass';
import useClasses from '../../../hooks/useClasses';
import BaseModal from '../../_molecules/BaseModal';

const NONE_VALUE = '__none__';

function AssignClassModal({
  open,
  onClose,
  gameId,
  currentClassId,
}: {
  open: boolean;
  onClose: () => void;
  gameId: string;
  currentClassId?: string | null;
}) {
  const { classes, isLoading } = useClasses();
  const { assignClass, isAssigning } = useAssignClass(gameId);
  const [selected, setSelected] = useState<string>(currentClassId ?? NONE_VALUE);

  // The modal stays mounted (only `open` toggles), so re-sync the selection to
  // the game's current assignment each time it opens — otherwise a stale choice
  // from a previous open could be persisted.
  useEffect(() => {
    if (open) {
      setSelected(currentClassId ?? NONE_VALUE);
    }
  }, [open, currentClassId]);

  const handleAssign = () => {
    const classId = selected === NONE_VALUE ? null : selected;
    assignClass(classId, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <BaseModal
      open={open}
      onCancel={onClose}
      centered
      width="440px"
      title={
        <ModalTitle>
          <T keyName="Assign class" defaultValue="Assign class" />
        </ModalTitle>
      }
    >
      <Body>
        <Description>
          <T
            keyName="Assign class description"
            defaultValue="Pick a class to seed this lesson's student roster."
          />
        </Description>

        {isLoading ? (
          <Centered>
            <Spin />
          </Centered>
        ) : (
          // Always render the "No class" option so a stale assignment can be
          // cleared even when the teacher has no classes.
          <Radio.Group
            value={selected}
            onChange={e => setSelected(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}
          >
            <Radio value={NONE_VALUE}>
              <T keyName="No class" defaultValue="No class" />
            </Radio>
            {classes.map(cls => (
              <Radio key={cls.id} value={cls.id}>
                {cls.name} ({cls.studentCount})
              </Radio>
            ))}
          </Radio.Group>
        )}

        {!isLoading && classes.length === 0 && (
          <EmptyState>
            <T
              keyName="No classes to assign"
              defaultValue="You don't have any classes yet. Create one from the Classes page first."
            />
          </EmptyState>
        )}

        <Actions>
          <Button onClick={onClose}>
            <T keyName="Cancel" defaultValue="Cancel" />
          </Button>
          <AssignButton
            type="primary"
            loading={isAssigning}
            onClick={handleAssign}
            disabled={isLoading}
          >
            <T keyName="Assign" defaultValue="Assign" />
          </AssignButton>
        </Actions>
      </Body>
    </BaseModal>
  );
}

export default AssignClassModal;

const ModalTitle = styled.span`
  font-weight: 700;
  font-size: 18px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 4px;
`;

const Description = styled.p`
  color: ${theme.colors.subtitleGrey};
  margin: 0;
  font-size: 14px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const EmptyState = styled.div`
  color: ${theme.colors.subtitleGrey};
  text-align: center;
  padding: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const AssignButton = styled(Button)`
  background-color: ${theme.colors.orange};
  border-color: ${theme.colors.orange};
  border-radius: 5px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.orangeDark} !important;
    border-color: ${theme.colors.orangeDark} !important;
  }
`;
